import React, {
  useContext,
  type PropsWithChildren,
  createContext,
  useCallback,
} from "react";

import {
  CREATE_HIDDEN_SALES_SAVED_VIEW_TEMPLATE,
  CREATE_SALES_SAVED_VIEW,
  DELETE_SALES_SAVED_VIEW,
  GET_SALES_SAVED_VIEWS,
  UPDATE_SALES_SAVED_VIEW,
  UPDATE_SALES_SAVED_VIEW_ORDERING,
  UPDATE_SALES_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS,
} from "@gql/sales-saved-views";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type CreateSalesSavedView,
  type CreateSalesSavedViewVariables,
  type DeleteSalesSavedView,
  type DeleteSalesSavedViewVariables,
  type GetSalesSavedViews,
  type UpdateSalesSavedView,
  type UpdateSalesSavedViewVariables,
  type UpdateSalesSavedViewOrdering,
  type UpdateSalesSavedViewOrderingVariables,
  type CreateHiddenSalesSavedViewTemplate,
  type CreateHiddenSalesSavedViewTemplateVariables,
  type UpdateSalesSavedViewOrderingToAllAccountManagers,
  type UpdateSalesSavedViewOrderingToAllAccountManagersVariables,
} from "@north-beam/nb-client-side";

import { type PageConfiguration } from "../context";

import { type ChartState } from "./use-chart-state";
import { type TableState } from "./use-table-state";

export type SavedView = {
  id: number;
  name: string;
  is_default: boolean;
  is_template: boolean;
  config: {
    pageConfiguration: PageConfiguration;
    tableState: TableState;
    chartState: ChartState;
  };
};

type SavedViewsContextType = {
  savedViews: SavedView[];
  loading: boolean;
};

const SalesSavedViewsContext = createContext<SavedViewsContextType>({
  savedViews: [],
  loading: false,
} as SavedViewsContextType);

export function SalesSavedViewsProvider({
  children,
}: PropsWithChildren<unknown>) {
  const { data, loading } = useNorthbeamQuery<GetSalesSavedViews>(
    GET_SALES_SAVED_VIEWS,
  );

  return (
    <SalesSavedViewsContext.Provider
      value={{ savedViews: data?.me?.salesSavedViews ?? [], loading }}
    >
      {children}
    </SalesSavedViewsContext.Provider>
  );
}

export const useSalesSavedViews = () => {
  const context = useContext(SalesSavedViewsContext);
  if (context === undefined) {
    throw new Error(
      "useSalesSavedViews must be used within a SalesSavedViewsContext",
    );
  }
  return context;
};

export const useSavedViewsQueries = () => {
  const { savedViews, loading: isSavedViewsLoading } = useSalesSavedViews();

  const [createSalesViewMutation] = useNorthbeamMutation<
    CreateSalesSavedView,
    CreateSalesSavedViewVariables
  >(CREATE_SALES_SAVED_VIEW, {
    update(cache, { data }) {
      const newSavedView = data?.createSalesSavedView;
      if (newSavedView) {
        cache.modify({
          id: "User:{}",
          fields: {
            salesSavedViews(existingSavedViews = []) {
              return [...existingSavedViews, newSavedView];
            },
          },
        });
      }
    },
  });

  const createSavedView = useCallback(
    async (name: SavedView["name"], config: SavedView["config"]) => {
      const { data } = await createSalesViewMutation({
        variables: { name, config },
      });
      return data?.createSalesSavedView;
    },
    [createSalesViewMutation],
  );

  const [deleteSalesViewMutation] = useNorthbeamMutation<
    DeleteSalesSavedView,
    DeleteSalesSavedViewVariables
  >(DELETE_SALES_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const deleteSavedView = useCallback(
    async (id: number) => deleteSalesViewMutation({ variables: { id } }),
    [deleteSalesViewMutation],
  );

  const [updateSalesViewMutation] = useNorthbeamMutation<
    UpdateSalesSavedView,
    UpdateSalesSavedViewVariables
  >(UPDATE_SALES_SAVED_VIEW, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const updateSavedView = useCallback(
    async ({
      id,
      name,
      config,
    }: {
      id: number;
      name?: string;
      config?: SavedView["config"];
    }) => updateSalesViewMutation({ variables: { id, name, config } }),
    [updateSalesViewMutation],
  );

  const [updateSalesViewOrderingMutation] = useNorthbeamMutation<
    UpdateSalesSavedViewOrdering,
    UpdateSalesSavedViewOrderingVariables
  >(UPDATE_SALES_SAVED_VIEW_ORDERING, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const [updateSalesViewOrderingToAllAccountManagersMutation] =
    useNorthbeamMutation<
      UpdateSalesSavedViewOrderingToAllAccountManagers,
      UpdateSalesSavedViewOrderingToAllAccountManagersVariables
    >(UPDATE_SALES_SAVED_VIEW_ORDERING_TO_ALL_ACCOUNT_MANAGERS, {
      awaitRefetchQueries: true,
      refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
    });

  const updateSavedViewOrdering = useCallback(
    async (ids: number[], options?: { syncAllAccountManagers?: boolean }) =>
      options?.syncAllAccountManagers
        ? updateSalesViewOrderingToAllAccountManagersMutation({
            variables: { ids },
          })
        : updateSalesViewOrderingMutation({ variables: { ids } }),
    [
      updateSalesViewOrderingMutation,
      updateSalesViewOrderingToAllAccountManagersMutation,
    ],
  );

  const [createHiddenSalesSavedViewTemplateMutation] = useNorthbeamMutation<
    CreateHiddenSalesSavedViewTemplate,
    CreateHiddenSalesSavedViewTemplateVariables
  >(CREATE_HIDDEN_SALES_SAVED_VIEW_TEMPLATE, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_SALES_SAVED_VIEWS }],
  });

  const createHiddenSavedViewTemplate = useCallback(
    async (id: number) =>
      createHiddenSalesSavedViewTemplateMutation({ variables: { id } }),
    [createHiddenSalesSavedViewTemplateMutation],
  );

  return {
    savedViews,
    isSavedViewsLoading: isSavedViewsLoading,
    updateSavedView,
    deleteSavedView,
    updateSavedViewOrdering,
    createHiddenSavedViewTemplate,
    createSavedView,
  };
};
